import React, { useRef, useEffect, useState } from "react";
import Webcam from "react-webcam";
import { FaceMesh } from "@mediapipe/face_mesh";
import * as cam from "@mediapipe/camera_utils";
import { useNavigate } from "react-router-dom";
import "../bch_integration";
import { api } from "../utils/api";
import { useLogs } from "../context/LogContext";
import { saveBCHRegistrationData } from "../utils/bchStorage";
import CryptoJS from "crypto-js";

function Scan() {
  const navigate = useNavigate();

  const { addLog, setChannelMeta } = useLogs();
  const webcamRef = useRef(null);

  const canvasRef = useRef(null);
  const leftArrowImage = useRef(null);
  const rightArrowImage = useRef(null);
  const rollrightArrowImage = useRef(null);
  const rollleftArrowImage = useRef(null);
  const upArrowImage = useRef(null);
  const downArrowImage = useRef(null);
  const [photos, setPhotos] = useState([]);
  const [upload] = useState(false);
  const [uploaded] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);
  const [, setFaceDetected] = useState(false);

  const TOTAL_FRAMES = 100;
  // Animation states
  const arrowAnimationRef = useRef({
    pitch: { opacity: 0, scale: 1, pulse: 0 },
    yaw: { opacity: 0, scale: 1, pulse: 0 },
    roll: { opacity: 0, scale: 1, pulse: 0 },
  });
  const centerDotAnimationRef = useRef({
    scale: 1,
    pulsePhase: 0,
    color: { r: 255, g: 0, b: 0 },
  });
  const particlesRef = useRef([]);

  const [center, setCenter] = useState(false);
  const [iod, setIod] = useState(0);
  const [, setTimeStamps] = useState([]);
  const [, setPitchData] = useState([]);
  const [, setYawData] = useState([]);
  const [, setRollData] = useState([]);
  const [, setEar] = useState(0);
  const [distances, setDistances] = useState([]);
  const [bchRegistrationData, setBchRegistrationData] = useState(null);
  const [bchReady, setBchReady] = useState(false);
  const lastFrameTimeRef = useRef(0);
  const iRef = useRef(0);
  const lastFaceDetectedRef = useRef(true);
  const cameraRef = useRef(null);
  const onResultsRef = useRef(null);
  const saltRef = useRef(
    CryptoJS.lib.WordArray.random(32).toString(CryptoJS.enc.Hex)
  );
  const capturedPhotoRef = useRef(false);
  const photoHashRef = useRef(null);
  const workflowLoggedRef = useRef(false);

  // Smooth animation helper
  const lerp = (start, end, factor) => {
    return start + (end - start) * factor;
  };

  // Update arrow animations smoothly
  const updateArrowAnimation = (type, shouldShow) => {
    const current = arrowAnimationRef.current[type];
    const targetOpacity = shouldShow ? 1 : 0;
    const targetScale = shouldShow ? 1 : 0.8;

    current.opacity = lerp(current.opacity, targetOpacity, 0.15);
    current.scale = lerp(current.scale, targetScale, 0.15);
    current.pulse = shouldShow ? (current.pulse + 0.1) % (Math.PI * 2) : 0;
  };

  // Draw arrow with smooth animation
  const drawAnimatedArrow = (ctx, img, x, y, width, height, animation) => {
    // Check if image is valid and loaded (not broken)
    if (!img || !img.complete || animation.opacity < 0.01) return;

    // Check if image is in broken state (naturalWidth/naturalHeight will be 0 if broken)
    if (img.naturalWidth === 0 || img.naturalHeight === 0) return;

    ctx.save();
    ctx.globalAlpha = animation.opacity;

    // Apply pulsing effect
    const pulseScale = 1 + Math.sin(animation.pulse) * 0.1;
    const finalScale = animation.scale * pulseScale;

    const scaledWidth = width * finalScale;
    const scaledHeight = height * finalScale;
    const offsetX = (width - scaledWidth) / 2;
    const offsetY = (height - scaledHeight) / 2;

    ctx.setTransform(1, 0, 0, -1, 0, 0);
    ctx.drawImage(img, x + offsetX, y + offsetY, scaledWidth, scaledHeight);
    ctx.restore();
  };

  // Update center dot animation
  const updateCenterDotAnimation = (isCentered) => {
    const anim = centerDotAnimationRef.current;

    // Target color
    const targetColor = isCentered
      ? { r: 0, g: 255, b: 0 } // Lime
      : { r: 255, g: 0, b: 0 }; // Red

    // Smooth color transition
    anim.color.r = lerp(anim.color.r, targetColor.r, 0.1);
    anim.color.g = lerp(anim.color.g, targetColor.g, 0.1);
    anim.color.b = lerp(anim.color.b, targetColor.b, 0.1);

    // Pulsing animation
    // anim.pulsePhase = (anim.pulsePhase + 0.08) % (Math.PI * 2);
    // anim.scale = 1 + Math.sin(anim.pulsePhase) * 0.2;

    // Create particles when centered
    if (isCentered && Math.random() < 0.3) {
      createParticle();
    }
  };

  // Create particles
  const createParticle = () => {
    const angle = Math.random() * Math.PI * 2;
    const speed = 1 + Math.random() * 2;
    particlesRef.current.push({
      x: 0,
      y: 0,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      life: 1.0,
      size: 2 + Math.random() * 3,
    });

    // Keep only recent particles
    if (particlesRef.current.length > 50) {
      particlesRef.current = particlesRef.current.slice(-50);
    }
  };

  // Draw particles
  const drawParticles = (ctx, cx, cy) => {
    particlesRef.current = particlesRef.current.filter((p) => {
      p.x += p.vx;
      p.y += p.vy;
      p.life -= 0.02;

      if (p.life <= 0) return false;

      ctx.save();
      ctx.globalAlpha = p.life;
      ctx.fillStyle = "#00ff00";
      ctx.shadowColor = "#00ff00";
      ctx.shadowBlur = 8;
      ctx.beginPath();
      ctx.arc(cx + p.x, cy + p.y, p.size, 0, 2 * Math.PI);
      ctx.fill();
      ctx.restore();

      return true;
    });
  };

  // Draw center dot with animation
  const drawCenterDot = (ctx, cx, cy) => {
    const anim = centerDotAnimationRef.current;

    ctx.save();

    // Outer glow
    const gradient = ctx.createRadialGradient(
      cx,
      cy,
      0,
      cx,
      cy,
      8 * anim.scale
    );
    const color = `rgb(${Math.round(anim.color.r)}, ${Math.round(
      anim.color.g
    )}, ${Math.round(anim.color.b)})`;
    gradient.addColorStop(0, color);
    gradient.addColorStop(
      0.5,
      `rgba(${Math.round(anim.color.r)}, ${Math.round(
        anim.color.g
      )}, ${Math.round(anim.color.b)}, 0.3)`
    );
    gradient.addColorStop(1, "transparent");

    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(cx, cy, 8 * anim.scale, 0, 2 * Math.PI);
    ctx.fill();

    // Inner dot
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(cx, cy, 5 * anim.scale, 0, 2 * Math.PI);
    ctx.fill();

    // White center highlight
    ctx.fillStyle = "rgba(255, 255, 255, 0.6)";
    ctx.beginPath();
    ctx.arc(cx - 1.5, cy - 1.5, 1.5, 0, 2 * Math.PI);
    ctx.fill();

    ctx.restore();
  };

  // Draw face landmarks with fade effect
  const drawFaceLandmarks = (ctx, landmarks, opacity = 1) => {
    ctx.save();
    ctx.globalAlpha = opacity;
    ctx.fillStyle = "white";
    ctx.shadowColor = "rgba(0, 188, 212, 0.8)";
    ctx.shadowBlur = 8;

    landmarks.forEach((point, index) => {
      ctx.beginPath();
      ctx.arc(point.x * -75, point.y * 100, 2.5, 0, 2 * Math.PI);
      ctx.fill();
    });

    ctx.restore();
  };

  // Draw connection lines between landmarks
  const drawFaceConnections = (ctx, landmarks) => {
    ctx.save();
    ctx.strokeStyle = "rgba(0, 188, 212, 0.3)";
    ctx.lineWidth = 1;
    ctx.shadowColor = "rgba(0, 188, 212, 0.5)";
    ctx.shadowBlur = 4;

    // Draw connections between consecutive points
    ctx.beginPath();
    for (let i = 0; i < landmarks.length - 1; i++) {
      ctx.moveTo(landmarks[i].x * -75, landmarks[i].y * 100);
      ctx.lineTo(landmarks[i + 1].x * -75, landmarks[i + 1].y * 100);
    }
    // Close the loop
    ctx.moveTo(
      landmarks[landmarks.length - 1].x * -75,
      landmarks[landmarks.length - 1].y * 100
    );
    ctx.lineTo(landmarks[0].x * -75, landmarks[0].y * 100);
    ctx.stroke();

    ctx.restore();
  };

  // Progress ring animation
  const drawProgressRing = (ctx, cx, cy, progress) => {
    const radius = 80;
    const lineWidth = 6;

    ctx.save();

    // Background ring
    ctx.strokeStyle = "rgba(255, 255, 255, 0.1)";
    ctx.lineWidth = lineWidth;
    ctx.beginPath();
    ctx.arc(cx, cy, radius, 0, 2 * Math.PI);
    ctx.stroke();

    // Progress ring
    const gradient = ctx.createLinearGradient(cx - radius, cy, cx + radius, cy);
    gradient.addColorStop(0, "#00bcd4");
    gradient.addColorStop(1, "#00e5ff");

    ctx.strokeStyle = gradient;
    ctx.lineWidth = lineWidth;
    ctx.lineCap = "round";
    ctx.shadowColor = "#00bcd4";
    ctx.shadowBlur = 15;

    const angle = (progress / 100) * 2 * Math.PI - Math.PI / 2;
    ctx.beginPath();
    ctx.arc(cx, cy, radius, -Math.PI / 2, angle);
    ctx.stroke();

    ctx.restore();
  };

  function isNoseCentered(results) {
    const cx = canvasRef.current.width / 2;
    const cy = canvasRef.current.height / 2;
    const nose = results.multiFaceLandmarks[0][4];

    const nx = nose.x * canvasRef.current.width;
    const ny = (1 - nose.y) * canvasRef.current.height;
    const distPix = Math.sqrt((nx - cx) ** 2 + (ny - cy) ** 2);

    return distPix <= 10;
  }
  function isPoseStable(pitch, yaw, roll) {
    return (
      Math.abs(pitch) <= 0.1 && Math.abs(yaw) <= 0.1 && Math.abs(roll) <= 0.1
    );
  }

  function computeIOD(pixelLandmarks) {
    const leftEyeOuter = pixelLandmarks[33];
    const rightEyeOuter = pixelLandmarks[263];

    const dx = rightEyeOuter.x - leftEyeOuter.x;
    const dy = rightEyeOuter.y - leftEyeOuter.y;

    const iod = Math.sqrt(dx * dx + dy * dy);
    setIod(iod);
    return iod >= 130 && iod <= 133;
  }

  function computePoseAngles(noseTipPoint, verticalLinePoint) {
    const { x, y } = noseTipPoint;
    const den = Math.sqrt(Math.max(0.0, 1.0 - y * y - x * x));

    const pitch = Math.atan2(y, den);
    const yaw = Math.atan2(x, den);
    const roll = 0;
    // const roll = Math.atan2(verticalLinePoint[1], verticalLinePoint[0]);

    return { pitch, yaw, roll };
  }

  const earCalc = (eyePoints) => {
    const left = [33, 160, 158, 133, 153, 144];
    const right = [362, 385, 387, 263, 373, 380];

    if (Math.max(...left.concat(right)) >= eyePoints.length) {
      return 0.0;
    }

    const A_left = Math.sqrt(
      Math.pow(eyePoints[160].x - eyePoints[144].x, 2) +
        Math.pow(eyePoints[160].y - eyePoints[144].y, 2)
    );
    const B_left = Math.sqrt(
      Math.pow(eyePoints[158].x - eyePoints[153].x, 2) +
        Math.pow(eyePoints[158].y - eyePoints[153].y, 2)
    );
    const C_left = Math.sqrt(
      Math.pow(eyePoints[33].x - eyePoints[133].x, 2) +
        Math.pow(eyePoints[33].y - eyePoints[133].y, 2)
    );
    const ear_left = (A_left + B_left) / (2.0 * C_left);

    const A_right = Math.sqrt(
      Math.pow(eyePoints[385].x - eyePoints[380].x, 2) +
        Math.pow(eyePoints[385].y - eyePoints[380].y, 2)
    );
    const B_right = Math.sqrt(
      Math.pow(eyePoints[387].x - eyePoints[373].x, 2) +
        Math.pow(eyePoints[387].y - eyePoints[373].y, 2)
    );
    const C_right = Math.sqrt(
      Math.pow(eyePoints[362].x - eyePoints[263].x, 2) +
        Math.pow(eyePoints[362].y - eyePoints[263].y, 2)
    );
    const ear_right = (A_right + B_right) / (2.0 * C_right);

    return (ear_left + ear_right) / 2.0;
  };

  const calculateDistance = (point1, point2) => {
    const x1 = point1.x,
      y1 = point1.y;
    const x2 = point2.x,
      y2 = point2.y;
    // Return as number (will be normalized to 0-255 in BCH processing)
    return Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));
  };

  const calculateLandmarkDistances = (
    normalizedPoints,
    mandatoryLandmarkPairs
  ) => {
    return mandatoryLandmarkPairs.map((pair) => {
      const point1 = normalizedPoints[pair[0]];
      const point2 = normalizedPoints[pair[1]];
      return calculateDistance(point1, point2);
    });
  };

  const uploadAllPhotos = async () => {
    // setUpload(true);
    // for (let i = 0; i < photos.length; i++) {
    //   const formData = new FormData();
    //   formData.append("file", photos[i]);
    //   formData.append("upload_preset", UPLOAD_PRESET);
    //   await axios.post(
    //     `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/upload`,
    //     formData
    //   );
    // }
    // setUpload(false);
    // setIsCapturing(false);
    // setUploaded(true);
    // setPhotos([]);
    // alert("All 30 Photos Uploaded Successfully!");
  };

  function drawScatterPlot(
    ctx,
    landmarks,
    pitch,
    yaw,
    roll,
    results,
    { color = "white", radius = 2, showIndex = false } = {}
  ) {
    const canvas = canvasRef.current;
    const width = canvas.width;
    const height = canvas.height;

    ctx.clearRect(0, 0, width, height);

    const cx = width / 2;
    const cy = height / 2;

    // Check if nose is centered
    let isCentered = false;
    if (results.multiFaceLandmarks && results.multiFaceLandmarks.length > 0) {
      const nose = results.multiFaceLandmarks[0][4];
      const nx = nose.x * width;
      const ny = (1 - nose.y) * height;
      const distPix = Math.sqrt((nx - cx) ** 2 + (ny - cy) ** 2);
      isCentered = distPix <= 10;
    }

    setCenter(isCentered);

    // Update animations
    updateCenterDotAnimation(isCentered);

    // Fill transparent background
    ctx.fillStyle = "transparent";
    ctx.fillRect(0, 0, width, height);

    // Save and transform context
    ctx.save();
    ctx.translate(width / 2, height / 2);
    ctx.scale(1, -1);

    // Draw face connections first (behind landmarks)
    if (landmarks) {
      drawFaceConnections(ctx, landmarks);
    }

    // Draw face landmarks
    if (landmarks) {
      drawFaceLandmarks(ctx, landmarks, 0.9);
    }

    // Update and draw arrows with animations
    updateArrowAnimation("pitch", Math.abs(pitch) > 0.1);
    updateArrowAnimation("yaw", Math.abs(yaw) > 0.1);
    updateArrowAnimation("roll", Math.abs(roll) > 0.1);

    if (pitch > 0.1) {
      drawAnimatedArrow(
        ctx,
        upArrowImage.current,
        260,
        -380,
        100,
        100,
        arrowAnimationRef.current.pitch
      );
    } else if (pitch < -0.1) {
      drawAnimatedArrow(
        ctx,
        downArrowImage.current,
        280,
        -180,
        100,
        100,
        arrowAnimationRef.current.pitch
      );
    }

    if (yaw > 0.1) {
      drawAnimatedArrow(
        ctx,
        rightArrowImage.current,
        380,
        -280,
        100,
        100,
        arrowAnimationRef.current.yaw
      );
    } else if (yaw < -0.1) {
      drawAnimatedArrow(
        ctx,
        leftArrowImage.current,
        180,
        -280,
        100,
        100,
        arrowAnimationRef.current.yaw
      );
    }

    // if (roll > 0.1) {
    //   drawAnimatedArrow(
    //     ctx,
    //     rollleftArrowImage.current,
    //     380,
    //     -220,
    //     100,
    //     100,
    //     arrowAnimationRef.current.roll
    //   );
    // } else if (roll < -0.1) {
    //   drawAnimatedArrow(
    //     ctx,
    //     rollrightArrowImage.current,
    //     180,
    //     -220,
    //     100,
    //     100,
    //     arrowAnimationRef.current.roll
    //   );
    // }

    ctx.restore();

    // Draw particles before center dot
    drawParticles(ctx, cx, cy);

    // Draw center dot (in normal coordinates)
    drawCenterDot(ctx, cx, cy);

    // Draw progress ring if capturing
    if (photos.length > 0 && photos.length < TOTAL_FRAMES) {
      drawProgressRing(ctx, cx, cy, photos.length);
    }
  }

  function onResults(results) {
    if (!webcamRef.current || !webcamRef.current.video) return;

    canvasRef.current.width = webcamRef.current.video.videoWidth;
    canvasRef.current.height = webcamRef.current.video.videoHeight;

    const canvasElement = canvasRef.current;
    const canvasCtx = canvasElement.getContext("2d");
    canvasCtx.save();

    canvasCtx.fillStyle = "transparent";
    canvasCtx.fillRect(0, 0, canvasElement.width, canvasElement.height);

    const face_oval = [
      10, 338, 297, 332, 284, 251, 389, 356, 454, 323, 361, 288, 397, 365, 379,
      378, 400, 377, 152, 148, 176, 149, 150, 136, 172, 58, 132, 93, 234, 127,
      162, 21, 54, 103, 67, 109,
    ];

    if (
      !results.multiFaceLandmarks ||
      results.multiFaceLandmarks.length === 0
    ) {
      if (lastFaceDetectedRef.current === true) {
        console.log("face lost → resetting photos and distances");

        setPhotos([]);
        setDistances([]);
        iRef.current = 0;
        lastFrameTimeRef.current = 0;
        capturedPhotoRef.current = false;
        photoHashRef.current = null;
        workflowLoggedRef.current = false;
        // Clear live meta so stale values don't remain on the right panel
        setChannelMeta?.("apple_face_id", { distanceHash: null, frame: null });
        setChannelMeta?.("mpc", { hash1: null, hash2: null, hash3: null, frame: null });
      }

      setFaceDetected(false);
      lastFaceDetectedRef.current = false;
      return;
    }

    if (results.multiFaceLandmarks) {
      setFaceDetected(true);
      lastFaceDetectedRef.current = true;
      for (const landmarks of results.multiFaceLandmarks) {
        const pixelLandmarks = landmarks.map((marks) => ({
          x: marks.x * canvasRef.current.width,
          y: (1 - marks.y) * canvasRef.current.height,
        }));

        let sumx = 0;
        let sumy = 0;
        pixelLandmarks.forEach((value, index) => {
          if (face_oval.includes(index)) {
            sumx += value.x;
            sumy += value.y;
          }
        });

        const avgx = sumx / face_oval.length;
        const avgy = sumy / face_oval.length;

        const pixelLandmarksCenter = pixelLandmarks.map((marks) => ({
          x: marks.x - avgx,
          y: marks.y - avgy,
        }));

        const sumOfSquares = pixelLandmarksCenter.map(
          (marks) => marks.x ** 2 + marks.y ** 2
        );
        const totalSum = sumOfSquares.reduce((acc, val) => acc + val, 0);
        const scale = Math.sqrt(totalSum / pixelLandmarksCenter.length);
        let normalizedPoints = pixelLandmarksCenter.map((marks) => ({
          x: marks.x / scale,
          y: marks.y / scale,
        }));

        let v = {
          x: normalizedPoints[263].x - normalizedPoints[33].x,
          y: normalizedPoints[263].y - normalizedPoints[33].y,
        };

        const rollV = Math.atan2(v.y, v.x);
        const rotationMatrix = [
          [Math.cos(rollV), Math.sin(rollV)],
          [-Math.sin(rollV), Math.cos(rollV)],
        ];
        normalizedPoints = normalizedPoints.map((point) => {
          return {
            x: point.x * rotationMatrix[0][0] + point.y * rotationMatrix[0][1],
            y: point.x * rotationMatrix[1][0] + point.y * rotationMatrix[1][1],
          };
        });
        // console.log("normalizedPoints", normalizedPoints);
        const normalizedPointsFace = normalizedPoints.filter((_, index) =>
          face_oval.includes(index)
        );

        const midlineMandatoryLandmarks = [2, 4, 9];
        const leftMandatoryLandmarks = [70, 107, 46, 55, 33, 133, 98];
        const rightMandatoryLandmarks = [300, 336, 276, 285, 263, 362, 327];

        const mandatoryLandmarks = [
          ...midlineMandatoryLandmarks,
          ...leftMandatoryLandmarks,
          ...rightMandatoryLandmarks,
        ];
        const optionalLandmarks = [
          423, 357, 349, 347, 340, 266, 330, 427, 280, 203, 128, 120, 118, 111,
          36, 101, 207, 50, 187, 147, 411, 376, 336, 107, 351, 399, 429, 363,
          134, 209, 174, 122, 151, 69, 299, 63, 156, 293, 383,
        ];
        const selectedLandmarks = optionalLandmarks.slice(0, 10);

        const getUniquePairs = (arr) => {
          const pairs = [];
          for (let i = 0; i < arr.length; i++) {
            for (let j = i + 1; j < arr.length; j++) {
              pairs.push([Math.min(arr[i], arr[j]), Math.max(arr[i], arr[j])]);
            }
          }
          return pairs;
        };

        const getConsecutivePairs = (arr) => {
          const pairs = [];
          for (let i = 0; i < arr.length; i++) {
            pairs.push([arr[i], arr[(i + 1) % arr.length]]);
          }
          return pairs;
        };

        const getProductPairs = (arr1, arr2) => {
          const pairs = [];
          for (let i = 0; i < arr1.length; i++) {
            for (let j = 0; j < arr2.length; j++) {
              pairs.push([arr1[i], arr2[j]]);
            }
          }
          return pairs;
        };

        const mandatoryLandmarkPairs = [
          ...getUniquePairs(mandatoryLandmarks),
          ...getConsecutivePairs(selectedLandmarks),
          ...getProductPairs(mandatoryLandmarks, selectedLandmarks),
        ];

        const calculatedDistances = calculateLandmarkDistances(
          normalizedPoints,
          mandatoryLandmarkPairs
        );

        // Ensure we have exactly 316 distances
        if (calculatedDistances.length !== 316) {
          console.warn(
            `Expected 316 distances, got ${calculatedDistances.length}`
          );
        }

        const point2 = normalizedPoints[263];
        const point3 = normalizedPoints[33];
        const deltaX = point2.x - point3.x;
        const deltaY = point2.y - point3.y;
        const { pitch, yaw, roll } = computePoseAngles(normalizedPoints[4], [
          deltaX,
          deltaY,
        ]);

        const newTimeStamp = Date.now();
        setTimeStamps((prev) => {
          if (prev.length >= 200) {
            return [...prev.slice(1), newTimeStamp];
          } else {
            return [...prev, newTimeStamp];
          }
        });

        setPitchData((prev) => {
          if (prev.length >= 200) {
            return [...prev.slice(1), pitch];
          } else {
            return [...prev, pitch];
          }
        });

        setYawData((prev) => {
          if (prev.length >= 200) {
            return [...prev.slice(1), yaw];
          } else {
            return [...prev, yaw];
          }
        });

        setRollData((prev) => {
          if (prev.length >= 200) {
            return [...prev.slice(1), roll];
          } else {
            return [...prev, roll];
          }
        });

        drawScatterPlot(
          canvasCtx,
          normalizedPointsFace,
          pitch,
          yaw,
          roll,
          results,
          { color: "white", radius: 2, showIndex: true }
        );

        if (iRef.current < TOTAL_FRAMES && photos.length < TOTAL_FRAMES) {
          const noseOK = isNoseCentered(results);
          const poseOK = isPoseStable(pitch, yaw, roll);
          const iod = computeIOD(pixelLandmarks);

          if (!noseOK && !poseOK) {
            iRef.current = 0;
            lastFrameTimeRef.current = 0;
            continue;
          }

          const now = Date.now();
          if (now - lastFrameTimeRef.current < 50) {
            continue;
          }

          if (noseOK && poseOK && photos.length < TOTAL_FRAMES && iod) {
            setIsCapturing(true);
            console.log("photos", iRef.current);

            // Log "workflow" style messages ONCE when registration capture begins
            if (!workflowLoggedRef.current) {
              workflowLoggedRef.current = true;
              addLog("ByoSync (Registration): Live face signal captured (ephemeral).", "byosync");
              addLog("ByoSync: Device-only non-invertible transform. No template stored.", "byosync");

              addLog("Aadhaar (Concept): User submits face image → template generated → stored centrally.", "aadhaar");
              addLog("Apple Face ID (Concept): Device-specific embedding stored on-device.", "apple_face_id");
              addLog("MPC (Concept): Biometric signal → cryptographic shares → distributed across servers.", "mpc");
            }

            if (webcamRef.current) {
              const imageSrc = webcamRef.current.getScreenshot();
              // Store a single photo preview for Aadhaar panel (once per registration scan)
              if (!capturedPhotoRef.current && imageSrc) {
                capturedPhotoRef.current = true;
                setChannelMeta?.("aadhaar", { photo: imageSrc });
                try {
                  photoHashRef.current = CryptoJS.SHA256(String(imageSrc)).toString();
                } catch (e) {
                  photoHashRef.current = null;
                }
              }
              setPhotos((prev) => {
                if (prev.length < TOTAL_FRAMES) {
                  return [...prev, imageSrc];
                }
                return prev;
              });
            }

            lastFrameTimeRef.current = now;
            const frameNum = iRef.current + 1;
            iRef.current += 1;

            // Apple Face ID: live distance hash during capture
            try {
              const hash = CryptoJS.SHA256(JSON.stringify(calculatedDistances)).toString();
              setChannelMeta?.("apple_face_id", { distanceHash: hash, frame: frameNum });
            } catch (e) {
              // ignore hash failures
            }

            // MPC: 3 hashes during capture (live)
            try {
              const h1 = CryptoJS.SHA256(JSON.stringify(calculatedDistances)).toString();
              const h2 = photoHashRef.current || CryptoJS.SHA256("no-photo").toString();
              const deviceKeyHash = localStorage.getItem("deviceKeyHash") || "";
              const h3 = CryptoJS.SHA256(
                `${String(frameNum)}|${String(deviceKeyHash)}|${String(saltRef.current)}`
              ).toString();
              setChannelMeta?.("mpc", { hash1: h1, hash2: h2, hash3: h3, frame: frameNum });
            } catch (e) {
              // ignore hash failures
            }

            setDistances((prev) => {
              // Store up to 30 distance arrays (one per photo)
              if (prev.length < TOTAL_FRAMES) {
                return [...prev, calculatedDistances];
              }
              return prev;
            });
          }
        }

        const calculatedEar = earCalc(normalizedPoints);
        setEar(calculatedEar);
      }
    }

    canvasCtx.restore();
  }

  // Keep the latest onResults handler without forcing the FaceMesh init effect to re-run.
  // (Assigning to a ref during render is safe and avoids exhaustive-deps warnings.)
  onResultsRef.current = onResults;

  useEffect(() => {
    const faceMesh = new FaceMesh({
      locateFile: (file) => {
        return `https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh/${file}`;
      },
    });

    faceMesh.setOptions({
      maxNumFaces: 1,
      refineLandmarks: true,
      minDetectionConfidence: 0.5,
      minTrackingConfidence: 0.5,
      selfieMode: true,
    });

    faceMesh.onResults((results) => {
      onResultsRef.current?.(results);
    });

    if (
      typeof webcamRef.current !== "undefined" &&
      webcamRef.current !== null
    ) {
      cameraRef.current = new cam.Camera(webcamRef.current.video, {
        onFrame: async () => {
          await faceMesh.send({ image: webcamRef.current.video });
        },
        width: "100%",
        height: "100%",
      });
      cameraRef.current.start();
    }

    return () => {
      try {
        cameraRef.current?.stop?.();
      } catch (e) {
        // ignore cleanup errors
      }
    };
  }, []);

  useEffect(() => {
    const loadImage = (imgRef, src) => {
      imgRef.current = new Image();
      imgRef.current.onload = () => {
        console.log(`Image loaded successfully: ${src}`);
      };
      imgRef.current.onerror = (error) => {
        console.error(`Failed to load image: ${src}`, error);
        // Set a flag to indicate the image is broken
        imgRef.current = null;
      };
      imgRef.current.src = src;
    };

    loadImage(
      leftArrowImage,
      "https://res.cloudinary.com/deepak2199/image/upload/v1768561055/teh2zqezgiig0hazmacv.png"
    );
    loadImage(
      rightArrowImage,
      "https://res.cloudinary.com/deepak2199/image/upload/v1768561055/uvhrqf4pv5whhs4iqrse.png"
    );
    loadImage(
      rollleftArrowImage,
      "https://res.cloudinary.com/deepak2199/image/upload/v1768561055/xbrveszed0qkpze4mxoz.png"
    );
    loadImage(
      rollrightArrowImage,
      "https://res.cloudinary.com/deepak2199/image/upload/v1768561055/hrfus7kjanpvvg996trw.png"
    );
    loadImage(
      upArrowImage,
      "https://res.cloudinary.com/deepak2199/image/upload/v1768561055/opicmo7rtqn2siyn543e.png"
    );
    loadImage(
      downArrowImage,
      "https://res.cloudinary.com/deepak2199/image/upload/v1768561055/yydbtemfa5derghpu2z6.png"
    );
  }, []);

  // Initialize BCH module on component mount
  useEffect(() => {
    const initBCHModule = async () => {
      try {
        if (
          window.BCHBiometric &&
          typeof window.BCHBiometric.initBCH === "function"
        ) {
          await window.BCHBiometric.initBCH();
          setBchReady(true);
          console.log("BCH module initialized successfully");
        } else {
          console.warn("BCHBiometric not available, retrying...");
          // Retry after a short delay
          setTimeout(initBCHModule, 1000);
        }
      } catch (error) {
        console.error("Failed to initialize BCH module:", error);
      }
    };

    initBCHModule();
  }, []);

  // Process distances with BCH when photos are complete
  useEffect(() => {
    const processBCHRegistration = async () => {
      if (
        photos.length === TOTAL_FRAMES &&
        distances.length > 0 &&
        bchReady &&
        !bchRegistrationData
      ) {
        try {
          console.log(
            "Processing BCH registration with",
            distances.length,
            "distance arrays"
          );

          // Process each distance array through BCH registration in a loop
          // Store as array of objects, where each object has: ecc, hashHex, r, helper
          const registrationDataArray = [];
          // Keep BCH registration progress logs in BYOSYNC (not MPC)
          addLog("100 frames captured...", "byosync");
          addLog(
            `Processing BCH registration with, ${distances.length}, distance arrays`,
            "byosync"
          );
          // Loop through each distance array
          for (let i = 0; i < distances.length; i++) {
            try {
              console.log(
                `Processing distance array ${i + 1}/${distances.length}`
              );

              // Process each distance array through BCH
              const registrationData =
                await window.BCHBiometric.registerBiometric(
                  distances[i],
                  saltRef.current
                );

              // Create object with token, k2, helper, secretHash
              const dataObject = {
                token: registrationData.token || "",
                k2: registrationData.k2 || "",
                helper: registrationData.helper || "",
                secretHash: registrationData.secretHash || "", // Store secretHash for direct verification
                timestamp:
                  registrationData.timestamp || new Date().toISOString(),
                index: i,
              };

              // Push object to array
              registrationDataArray.push(dataObject);

              console.log(`Completed processing array ${i + 1}:`, {
                k2: dataObject.k2 || 0,
                helper: dataObject.helper?.length || 0,
                token: dataObject.token || 0,
                secretHash: dataObject.secretHash?.substring(0, 16) + "...",
              });
              // if (i === 0) {
              //   addLog(`Completed processing array ${i + 1}:, {
              //   r: ${dataObject.r?.length},
              //   helper: ${dataObject.helper?.length},
              //   ecc: ${dataObject.ecc?.length},
              //   hashHex:${dataObject.hashHex},
              // }`);
              // }
            } catch (error) {
              console.error(`Error processing distance array ${i + 1}:`, error);
              // Continue with next array even if one fails
            }
          }

          // Final registration data is an array of objects
          const finalRegistrationData = registrationDataArray;

          addLog(`BCH registration data generated (array of objects):, {
                      totalObjects: ${finalRegistrationData.length},
                      sampleObject: {
                        hasToken: ${finalRegistrationData[0].token},
                        hasK2: ${finalRegistrationData[0].k2},
                        hasHelper: ${finalRegistrationData[0].helper.length},
                      },
                    }`, "byosync");

          console.log("BCH registration data generated (array of objects):", {
            totalObjects: finalRegistrationData.length,
            sampleObject: finalRegistrationData[0]
              ? {
                  hasToken: !!finalRegistrationData[0].token,
                  hasK2: !!finalRegistrationData[0].k2,
                  hasHelper: !!finalRegistrationData[0].helper,
                }
              : "No data",
          });

          setBchRegistrationData(finalRegistrationData);

          // Store in localStorage using storage utility
          saveBCHRegistrationData(finalRegistrationData);

          // Also store directly to localStorage as backup
          try {
            localStorage.setItem(
              "bchRegistrationData",
              JSON.stringify(finalRegistrationData)
            );
            localStorage.setItem("Salt", JSON.stringify(saltRef.current));
            console.log(
              "BCH registration data stored directly to localStorage:",
              finalRegistrationData.length,
              "objects"
            );
          } catch (error) {
            console.error(
              "Failed to store BCH registration data to localStorage:",
              error
            );
          }
        } catch (error) {
          console.error("BCH registration processing failed:", error);
        }
      }
    };

    processBCHRegistration();
  }, [photos.length, distances, bchReady, bchRegistrationData, addLog]);

  useEffect(() => {
    if (photos.length === TOTAL_FRAMES && bchRegistrationData) {
      uploadAllPhotos();
    }
  }, [photos, bchRegistrationData]);

  useEffect(() => {
    console.log("bchRegistrationData", bchRegistrationData);
  }, [bchRegistrationData]);

  // Send BCH registration data to backend
  useEffect(() => {
    if (bchRegistrationData) {
      console.log("bchRegistrationData", bchRegistrationData);
      const rawUserId = localStorage.getItem("userId");
      // Backward-compatible with older storage format: JSON.stringify("id") -> "\"id\""
      let userId = rawUserId;
      try {
        userId = JSON.parse(rawUserId);
      } catch (e) {
        // keep raw string
      }
      console.log("userId", userId);
      const sendBCHDataToBackend = async () => {
        try {
          const response = await api.post(`/users/addFaceId`, {
            userId,
            salt: saltRef.current,
            faceId: bchRegistrationData,
          });
          console.log("response", response);
          addLog(`BCH registration data sent to backend successfully`);
          addLog(`Response: Successfully added `);
          navigate("/");
        } catch (error) {
          console.error("API Error:", error);
          addLog(`API Error: ${error.message}`);
          if (error.response) {
            addLog(`Error response: ${JSON.stringify(error.response.data)}`);
            addLog(`Error status: ${error.response.status}`);
          }
        }
      };

      sendBCHDataToBackend();
    }
  }, [bchRegistrationData, addLog, navigate]);

  if (upload) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          background:
            "linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)",
          color: "#00bcd4",
          fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif",
        }}
      >
        {/* Spinner */}
        <div
          style={{
            width: "80px",
            height: "80px",
            border: "8px solid rgba(0, 188, 212, 0.2)",
            borderTop: "8px solid #00bcd4",
            borderRadius: "50%",
            animation: "spin 1s linear infinite",
            marginBottom: "30px",
            boxShadow: "0 0 30px rgba(0, 188, 212, 0.3)",
          }}
        />
        <h2
          style={{
            margin: 0,
            fontSize: "32px",
            fontWeight: "700",
            background: "linear-gradient(135deg, #00bcd4, #00e5ff)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}
        >
          Uploading Photos
        </h2>
        <p
          style={{
            margin: "12px 0 0 0",
            fontSize: "16px",
            color: "rgba(255, 255, 255, 0.6)",
          }}
        >
          Please wait while we upload your photos...
        </p>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (uploaded) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          background:
            "linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)",
          color: "#00bcd4",
          fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif",
        }}
      >
        {/* Success checkmark */}
        <div
          style={{
            width: "100px",
            height: "100px",
            borderRadius: "50%",
            background: "linear-gradient(135deg, #00bcd4, #00e5ff)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: "30px",
            animation: "scaleIn 0.5s ease-out",
            boxShadow: "0 0 40px rgba(0, 188, 212, 0.5)",
          }}
        >
          <svg
            width="60"
            height="60"
            viewBox="0 0 24 24"
            fill="none"
            stroke="white"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
        </div>
        <h2
          style={{
            margin: 0,
            fontSize: "36px",
            fontWeight: "700",
            background: "linear-gradient(135deg, #00bcd4, #00e5ff)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}
        >
          Upload Complete!
        </h2>
        <p
          style={{
            margin: "12px 0 0 0",
            fontSize: "18px",
            color: "rgba(255, 255, 255, 0.7)",
          }}
        >
          Thank you for contributing
        </p>
        <style>{`
          @keyframes scaleIn {
            0% {
              transform: scale(0);
              opacity: 0;
            }
            50% {
              transform: scale(1.1);
            }
            100% {
              transform: scale(1);
              opacity: 1;
            }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div
      className="left-section-scan"
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        width: "100%",
        height: "100%",
        minHeight: "100vh",
        // background:
        // "linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)",
        color: "white",
        padding: "20px",
        fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif",
        boxSizing: "border-box",
        gap: "20px",
      }}
    >
      {/* Top - Instructions */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: "20px",
          width: "100%",
          flexWrap: "wrap",
        }}
      >
        {/* Distance Instruction */}
        {(iod < 130 || iod > 133) && (
          <div
            style={{
              padding: "12px 24px",
              background: "rgba(255, 193, 7, 0.15)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(255, 193, 7, 0.4)",
              borderRadius: "12px",
              display: "flex",
              alignItems: "center",
              gap: "10px",
              animation: "pulse 2s infinite",
              boxShadow: "0 4px 20px rgba(255, 193, 7, 0.3)",
            }}
          >
            <div
              style={{
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                background: "#ffc107",
                boxShadow: "0 0 10px #ffc107",
              }}
            />
            <span
              style={{
                fontSize: "16px",
                fontWeight: "600",
                color: "#ffc107",
                textShadow: "0 0 10px rgba(255, 193, 7, 0.5)",
              }}
            >
              {iod < 130 ? "📏 Move closer" : iod > 133 ? "📏 Move away" : ""}
            </span>
          </div>
        )}

        {/* Nose Tip Instruction */}
        {!center && (
          <div
            style={{
              padding: "12px 24px",
              background: "rgba(255, 193, 7, 0.15)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(255, 193, 7, 0.4)",
              borderRadius: "12px",
              display: "flex",
              alignItems: "center",
              gap: "10px",
              animation: "pulse 2s infinite",
              boxShadow: "0 4px 20px rgba(255, 193, 7, 0.3)",
            }}
          >
            <div
              style={{
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                background: "#ffc107",
                boxShadow: "0 0 10px #ffc107",
              }}
            />
            <span
              style={{
                fontSize: "16px",
                fontWeight: "600",
                color: "#ffc107",
                textShadow: "0 0 10px rgba(255, 193, 7, 0.5)",
              }}
            >
              {"Nose tip on Red Dot"}
            </span>
          </div>
        )}
      </div>

      {/* Center - Camera Frame */}
      <div
        style={{
          position: "relative",
          width: "100%",
          maxWidth: "100%",
          height: "auto",
          maxHeight: "calc(100vh - 200px)",
          aspectRatio: "16 / 9",
          borderRadius: "24px",
          overflow: "hidden",
          boxShadow:
            "0 20px 60px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(0, 188, 212, 0.3)",
          border: "2px solid transparent",
          background:
            "linear-gradient(135deg, rgba(0, 188, 212, 0.1), rgba(0, 229, 255, 0.1))",
        }}
      >
        <Webcam
          ref={webcamRef}
          screenshotFormat="image/jpeg"
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            transform: "scaleX(-1)",
          }}
        />
        <canvas
          ref={canvasRef}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "transparent",
            zIndex: 9,
            transform: "scaleX(-1)",
          }}
        ></canvas>

        {/* Corner Overlays */}
        <div
          style={{
            position: "absolute",
            top: "20px",
            left: "20px",
            width: "40px",
            height: "40px",
            borderTop: "3px solid #00bcd4",
            borderLeft: "3px solid #00bcd4",
            borderRadius: "4px 0 0 0",
          }}
        />
        <div
          style={{
            position: "absolute",
            top: "20px",
            right: "20px",
            width: "40px",
            height: "40px",
            borderTop: "3px solid #00bcd4",
            borderRight: "3px solid #00bcd4",
            borderRadius: "0 4px 0 0",
          }}
        />
        <div
          style={{
            position: "absolute",
            bottom: "20px",
            left: "20px",
            width: "40px",
            height: "40px",
            borderBottom: "3px solid #00bcd4",
            borderLeft: "3px solid #00bcd4",
            borderRadius: "0 0 0 4px",
          }}
        />
        <div
          style={{
            position: "absolute",
            bottom: "20px",
            right: "20px",
            width: "40px",
            height: "40px",
            borderBottom: "3px solid #00bcd4",
            borderRight: "3px solid #00bcd4",
            borderRadius: "0 0 4px 0",
          }}
        />
      </div>

      {/* Bottom - Progress Bar */}
      <div
        style={{
          width: "90%",
          maxWidth: "100%",
          padding: "16px 32px",
          background: "rgba(0, 0, 0, 0.4)",
          backdropFilter: "blur(10px)",
          border: "1px solid rgba(0, 188, 212, 0.3)",
          borderRadius: "16px",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.5)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "10px",
          }}
        >
          <span
            style={{
              fontSize: "14px",
              color: "rgba(255, 255, 255, 0.8)",
              fontWeight: "500",
            }}
          >
            Progress
          </span>
          <span
            style={{
              fontSize: "18px",
              fontWeight: "700",
              background: "linear-gradient(135deg, #00bcd4, #00e5ff)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            {photos.length} / {TOTAL_FRAMES}
          </span>
        </div>
        <div
          style={{
            width: "100%",
            height: "8px",
            background: "rgba(255, 255, 255, 0.1)",
            borderRadius: "4px",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              width: `${(photos.length / TOTAL_FRAMES) * 100}%`,
              height: "100%",
              background: "linear-gradient(90deg, #00bcd4, #00e5ff)",
              borderRadius: "4px",
              transition: "width 0.3s ease",
              boxShadow: "0 0 10px rgba(0, 188, 212, 0.5)",
            }}
          />
        </div>
        {isCapturing && (
          <p
            style={{
              marginTop: "10px",
              fontSize: "12px",
              color: "#00ff00",
              textAlign: "center",
              animation: "pulse 1.5s infinite",
              fontWeight: "500",
            }}
          >
            🔴 Capturing...
          </p>
        )}
      </div>

      {/* Add CSS animation keyframes */}
      <style>{`
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }
      `}</style>
    </div>
  );
}

export default Scan;
